import { Breakpoints, CTA } from '@volkswagen-onehub/components-core';
import {
  Calendar,
  Internet,
  Navigate,
  Phone,
  useIsMobile,
} from '@vw-marketing/us-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useMyVwContext } from '../../../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../../../shared/hooks/use-tracking-manager';
import { onVWBasicDealerCardButtonClickEvent } from '../../../../shared/tagging/tagging-helpers';
import {
  StyledQuickActionContainer,
  StyledQuickActionsContainer,
  StyledQuickActionTextContainer,
} from './styles';
import { useDealer } from '../../../../app/providers/DealerContext';
import { useVehicle } from '../../../../app/providers/VehicleContext';
import { useFeatureServices } from '../../../../shared/hooks/use-feature-services';

const QuickActions = () => {
  const { translationHandler, authService } = useMyVwContext();
  const trackingManager = useTrackingManager();
  const isMobile = useIsMobile(Breakpoints.b560);
  const { userPreferredVehicle } = useVehicle();
  const { userPreferredDealer } = useDealer();
  const [loadingSchedulingURL, setLoadingSchedulingURL] =
    useState<boolean>(false);
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const dealerAddress = useMemo(
    () => `${userPreferredDealer?.address.address1 || ''} ${userPreferredDealer?.address.city || ''}, ${userPreferredDealer?.address.state || ''} ${userPreferredDealer?.address.zip || ''}`,
    [userPreferredDealer?.address]
  );

  // Get tracking process data for dealer details page
  const getTrackingProcessData = useCallback(() => {
    return {
      Name: 'Find a Dealer',
      StepName: 'Find a Dealer Map - Dealer Detail',
      StepNumber: '3.1',
    };
  }, []);

  // Track dealer card button clicks
  const trackDealerCardButtonClick = useCallback((linkName: string) => {
    try {
      onVWBasicDealerCardButtonClickEvent(
        trackingManager,
        getTrackingProcessData(),
        { name: linkName }
      );
    } catch (error) {
      console.warn('Error tracking dealer card button click:', error);
    }
  }, [trackingManager, getTrackingProcessData]);

  const handleServiceOnClick = async () => {
    // Track the button click
    trackDealerCardButtonClick('Service');

    if (!userPreferredVehicle) return;
    setLoadingSchedulingURL(true);
    try {
      const vehicleInfo = localeService.countryCode === 'CA' ? userPreferredVehicle?.vehicleId : userPreferredVehicle?.vin;
      const { getServiceSchedulingUrl } =
        await authService.getServiceSchedulingUrl(
          vehicleInfo,
          userPreferredDealer?.id,
          localeCode
        );

      if (!getServiceSchedulingUrl) return;

      window.open(getServiceSchedulingUrl.schedulingURL || '', '_blank');
    } catch (error) {
      console.error(
        'There was an error fetching service URL on dealer card',
        error,
      );
    } finally {
      setLoadingSchedulingURL(false);
    }
  };

  const handleCallOnClick = () => {
    // Track the button click
    trackDealerCardButtonClick('Call');

    window.open(`tel:${userPreferredDealer?.phone}`, '_blank');
  };

  const handleWebSiteOnClick = () => {
    // Track the button click
    trackDealerCardButtonClick('Website');

    window.open(userPreferredDealer?.url, '_blank');
  };

  const handleRouteOnClick = () => {
    // Track the button click
    trackDealerCardButtonClick('Route');

    const destinationAddress = dealerAddress?.trim();
    const destination = destinationAddress && destinationAddress.length > 0
      ? encodeURIComponent(destinationAddress)
      : '';

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const getTranslationKey = (action: 'service' | 'website' | 'route') => {
    const prefix = 'dealerManager.details.quickActions';
    const size = isMobile ? 'short' : 'long';
    return `${prefix}.${size}.${action}`;
  };

  return (
    <StyledQuickActionsContainer>
      <>
        <StyledQuickActionContainer>
          <CTA
            emphasis="primary"
            size="large"
            tag="button"
            icon={<Calendar />}
            onClick={handleServiceOnClick}
            disabled={loadingSchedulingURL}
            aria-label={translationHandler(getTranslationKey('service'))}
          />
          <StyledQuickActionTextContainer>
            {translationHandler(getTranslationKey('service'))}
          </StyledQuickActionTextContainer>
        </StyledQuickActionContainer>
        {isMobile && (
          <StyledQuickActionContainer>
            <CTA
              emphasis="secondary"
              tag="button"
              icon={<Phone />}
              onClick={handleCallOnClick}
              aria-label={translationHandler('dealerManager.details.quickActions.call')}
            />
            <StyledQuickActionTextContainer>
              {translationHandler('dealerManager.details.quickActions.call')}
            </StyledQuickActionTextContainer>
          </StyledQuickActionContainer>
        )}
      </>
      <>
        <StyledQuickActionContainer>
          <CTA
            emphasis="secondary"
            tag="button"
            icon={<Internet />}
            onClick={handleWebSiteOnClick}
            aria-label={translationHandler(getTranslationKey('website'))}
          />
          <StyledQuickActionTextContainer>
            {translationHandler(getTranslationKey('website'))}
          </StyledQuickActionTextContainer>
        </StyledQuickActionContainer>
        <StyledQuickActionContainer>
          <CTA
            emphasis="secondary"
            tag="button"
            icon={<Navigate />}
            onClick={handleRouteOnClick}
            aria-label={translationHandler(getTranslationKey('route'))}
          />
          <StyledQuickActionTextContainer>
            {translationHandler(getTranslationKey('route'))}
          </StyledQuickActionTextContainer>
        </StyledQuickActionContainer>
      </>
    </StyledQuickActionsContainer>
  );
};

export default QuickActions;